import { Link } from 'react-router-dom';
import {
  Envelope,
  Phone,
  MapPin,
  InstagramLogo,
  ArrowUpRight,
  CalendarPlus,
} from '@phosphor-icons/react';
import OutdoorAfroBadge from '@/components/v3/OutdoorAfroBadge';

const programs = [
  { label: 'Basketball Training', to: '/services' },
  { label: 'Private Swim Lessons', to: '/services' },
  { label: 'Swim Scholarship', to: '/scholarship' },
  { label: 'Strength & Conditioning', to: '/services' },
  { label: 'Shallow Water Aerobics', to: '/services' },
  { label: 'Group Lifeguarding Class', to: '/services' },
];

const quickLinks = [
  { label: 'About', to: '/about' },
  { label: 'Services', to: '/services' },
  { label: 'Book a Session', to: '/book' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Contact', to: '/contact' },
];

const socials = [
  { handle: '@tootall614', name: 'Jalen', href: 'https://instagram.com/tootall614' },
  { handle: '@sukisinclair', name: 'Sinclair', href: 'https://instagram.com/sukisinclair.robinson' },
];

export default function FooterV3() {
  return (
    <footer className="relative overflow-hidden bg-slate-950">
      {/* Subtle background mesh */}
      <div className="absolute inset-0 opacity-50 pointer-events-none bg-gradient-mesh-dark" />
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-20 h-[28rem] w-[28rem] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
      {/* Grain */}
      <div className="absolute inset-0 bg-noise opacity-[0.04] mix-blend-overlay pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        {/* Top: call to action band */}
        <div className="mb-14 flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-cyan-300/80 mb-2">
              Ready when you are
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tightest text-white max-w-xl">
              Train hard. Grow together.
            </h2>
          </div>
          <Link
            to="/book"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors shadow-xl"
          >
            <CalendarPlus size={20} weight="fill" />
            Book a Session
          </Link>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          {/* Brand block — wider */}
          <div className="md:col-span-5 space-y-5">
            <Link to="/" className="inline-flex items-center gap-2.5 group">
              <img
                src="/logo-btt.svg"
                alt="Better Together Training"
                className="w-10 h-10 drop-shadow-md transition-transform group-hover:scale-105"
              />
              <div className="flex flex-col leading-tight">
                <span className="font-display text-xl font-bold tracking-tightest text-white">
                  Better Together Training
                </span>
                <span className="text-[10px] font-semibold text-blue-200/70 tracking-[0.2em] uppercase">
                  Family Sports &amp; Fitness
                </span>
              </div>
            </Link>

            <p className="text-sm text-slate-400 leading-relaxed max-w-md">
              Columbus, Ohio&rsquo;s premier basketball, swimming, and strength training program. Family-focused
              coaching that builds confidence, character, and championship habits.
            </p>

            {/* Outdoor Afro partnership */}
            <OutdoorAfroBadge className="mt-1" />

            {/* Socials */}
            <div className="flex flex-col gap-2.5 pt-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-blue-200/60">
                Follow the coaches
              </p>
              <div className="flex flex-wrap gap-2">
                {socials.map((s) => (
                  <a
                    key={s.handle}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
                  >
                    <InstagramLogo size={16} weight="fill" className="text-pink-300" />
                    <span className="text-xs font-semibold">{s.handle}</span>
                    <span className="text-[10px] text-slate-500 group-hover:text-slate-300 transition-colors">
                      · {s.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Programs */}
          <div className="md:col-span-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.25em] text-blue-200/70 mb-4">
              Programs
            </h3>
            <ul className="space-y-2.5">
              {programs.map((p) => (
                <li key={p.label}>
                  <Link
                    to={p.to}
                    className="group inline-flex items-start gap-1.5 text-sm text-slate-300 hover:text-white transition-colors"
                  >
                    <span>{p.label}</span>
                    <ArrowUpRight
                      size={12}
                      weight="bold"
                      className="mt-1 opacity-0 -translate-x-1 group-hover:opacity-60 group-hover:translate-x-0 transition-all"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.25em] text-blue-200/70 mb-4">
              Visit
            </h3>
            <ul className="space-y-2.5">
              {quickLinks.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm text-slate-300 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.25em] text-blue-200/70 mb-4">
              Get in touch
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="tel:+16148697812"
                  className="group flex items-start gap-2.5 text-sm text-slate-300 hover:text-white transition-colors"
                >
                  <Phone size={16} weight="duotone" className="mt-0.5 flex-shrink-0 text-blue-300" />
                  <span>(614) 869-7812</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:mr.robinson3704@gmail.com"
                  className="group flex items-start gap-2.5 text-sm text-slate-300 hover:text-white transition-colors"
                >
                  <Envelope size={16} weight="duotone" className="mt-0.5 flex-shrink-0 text-blue-300" />
                  <span className="break-all">mr.robinson3704@gmail.com</span>
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-sm text-slate-300">
                <MapPin size={16} weight="duotone" className="mt-0.5 flex-shrink-0 text-blue-300" />
                <span>Columbus, Ohio</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between text-xs">
          <p className="text-slate-500">
            © {new Date().getFullYear()} Better Together Training LLC. All rights reserved.
          </p>
          <p className="text-slate-500">
            Site design by{' '}
            <a
              href="https://xigusa.com/solutions/website-design"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-slate-300 hover:text-white transition-colors"
            >
              XIGUSA
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
